import { useState, useEffect, lazy, Suspense } from "react";
import axios from "axios";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import Step1CompanyInfo from "./Step1CompanyInfo";
import Step2Agreement from "./Step2Agreement";
import Step3Payment from "./Step3Payment";
import Step4PaymentProof from "./Step4Verification";
import PaymentStatus from "./PaymentStatus";
import BuyerLogin from "./buyer/BuyerLogin";
import BuyerSetupPassword from "./buyer/BuyerSetupPassword";
import BuyerForgotPassword from "./buyer/BuyerForgotPassword";
import BuyerResetPassword from "./buyer/BuyerResetPassword";
import BuyerDashboard from "./buyer/BuyerDashboard";
import OnboardingShell from "./components/OnboardingShell";
import { API_BASE_URL } from "./config";

const AdminLogin = lazy(() => import("./admin/AdminLogin"));
const AdminDashboard = lazy(() => import("./admin/AdminDashboard"));

function OnboardingFlow() {
  const [step, setStep] = useState(1);

  const [previewLoading, setPreviewLoading] = useState(false);

  const handleViewAgreement = async () => {
    try {
      setPreviewLoading(true);
      const step1 = JSON.parse(localStorage.getItem("step1Data") || localStorage.getItem("finalStep1Data") || "{}");
      const step2 = JSON.parse(localStorage.getItem("step2Data") || localStorage.getItem("finalStep2Data") || "{}");
      const category = step2.category || localStorage.getItem("productCategory");

      // Check if Admin set a custom uploaded Agreement PDF for this product
      const adminProducts = JSON.parse(localStorage.getItem("vrm_admin_products") || "[]");
      const selectedAdminProduct = adminProducts.find(
        (p) => p.slug === category || 
               p.name.toLowerCase() === (category || "").toLowerCase() ||
               (p.category && p.category.toLowerCase() === (category || "").toLowerCase())
      );

      if (selectedAdminProduct && selectedAdminProduct.agreementPdf && selectedAdminProduct.agreementPdf.dataUrl) {
        const win = window.open();
        if (win) {
          win.document.write(`<html><head><title>${selectedAdminProduct.name} Agreement</title></head><body style="margin:0;"><iframe src="${selectedAdminProduct.agreementPdf.dataUrl}" frameborder="0" style="border:0; top:0px; left:0px; bottom:0px; right:0px; width:100%; height:100vh;" allowfullscreen></iframe></body></html>`);
        } else {
          window.open(selectedAdminProduct.agreementPdf.dataUrl, "_blank");
        }
        setPreviewLoading(false);
        return;
      }
      
      const selectedProducts = step2.selectedProducts || {};
      let totalAmount = 0;
      let interviewCount = 0;
      
      let workflowAiDetails = [];
      let aiBuddyDetails = [];
      let workflowAiCredits = 0;
      
      Object.keys(selectedProducts).forEach(key => {
         const qty = Number(selectedProducts[key] || 0);
         interviewCount += qty;
         
         if (key === 'videosage') { totalAmount += qty * 50; workflowAiCredits += qty; workflowAiDetails.push(`VideoSage: ${qty}`); }
         else if (key === 'screensage') { totalAmount += qty * 40; workflowAiCredits += qty; workflowAiDetails.push(`ScreenSage: ${qty}`); }
         else if (key === 'codesage') { totalAmount += qty * 60; workflowAiCredits += qty; workflowAiDetails.push(`CodeSage: ${qty}`); }
         else if (key === 'aibuddy_basic') { totalAmount += qty * 4500; aiBuddyDetails.push(`Basic: ${qty} Mo`); }
         else if (key === 'aibuddy_premium') { totalAmount += qty * 6500; aiBuddyDetails.push(`Premium: ${qty} Mo`); }
         else if (key === 'visionix_ai' || key === 'face_recognition') { totalAmount += qty * 1; aiBuddyDetails.push(`Visionix AI: ${qty} Mo`); }
      });
      
      let breakdownString = "";
      if (workflowAiDetails.length > 0) {
          breakdownString += `Workflow AI [${workflowAiDetails.join(", ")} | Total Credits: ${workflowAiCredits}] `;
      }
      if (aiBuddyDetails.length > 0) {
          breakdownString += `[${aiBuddyDetails.join(", ")}]`;
      }
      
      const payload = {
        companyName: step1.companyName || "",
        address: step1.address || "",
        companyEmail: step1.companyEmail || "",
        phone: step1.phone || "",
        agreementSignature: step2.signature || "",
        interviewCount: interviewCount,
        totalAmount: totalAmount,
        purchaseDetails: breakdownString.trim(),
        productCategory: step2.category || "workflow_ai",
        purchaserName: step1.purchaserName || "",
        purchaserDesignation: step1.purchaserDesignation || "",
        expectedGoLiveDate: step2.goLiveDate || ""
      };


      const res = await axios.post(
        `${API_BASE_URL}/onboarding/agreement/preview`,
        payload,
        { responseType: "blob" }
      );
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (err) {
      console.error(err);
      alert("Couldn't load the agreement preview. Please try again.");
    } finally {
      setPreviewLoading(false);
    }
  };

  if (step === 5) {
    return (
      <OnboardingShell step={5} title="">
        <div className="success-container">
          <div className="success-seal-wrap">
            <div className="success-seal-glow"></div>
            <div className="success-seal">
              <svg xmlns="http://www.w3.org/2000/svg" width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
            </div>
          </div>
          <h2 className="success-title">
            Onboarding Submitted
          </h2>
          <p className="success-subtext">
            Our team will verify your payment and details shortly.<br/>
            You'll receive your account credentials by email once confirmed.
          </p>
          <div className="success-actions" style={{ flexDirection: 'column', gap: '16px', alignItems: 'center' }}>
            <button
              className="onboard-button onboard-button--primary"
              type="button"
              onClick={handleViewAgreement}
              disabled={previewLoading}
              style={{ width: '100%', maxWidth: '260px' }}
            >
              {previewLoading 
                ? "Preparing agreement…" 
                : ((JSON.parse(localStorage.getItem("step2Data") || localStorage.getItem("finalStep2Data") || "{}").category || "workflow_ai") === "workflow_ai" 
                    ? "View Workflow AI Agreement ↗" 
                    : ((JSON.parse(localStorage.getItem("step2Data") || localStorage.getItem("finalStep2Data") || "{}").category || "") === "visionix_ai" || (JSON.parse(localStorage.getItem("step2Data") || localStorage.getItem("finalStep2Data") || "{}").category || "") === "face_recognition"
                        ? "View Visionix AI Agreement ↗"
                        : "View AI Buddy Agreement ↗"))}
            </button>
            <button
              className="onboard-button secondary"
              type="button"
              onClick={() => {
                localStorage.clear();
                setStep(1);
              }}
              style={{ width: '100%', maxWidth: '260px' }}
            >
              Start a new onboarding
            </button>
          </div>
        </div>
      </OnboardingShell>
    );
  }

  return (
    <div>
      {step === 1 && <Step1CompanyInfo onNext={() => setStep(2)} />}
      {step === 2 && <Step2Agreement onNext={() => setStep(3)} onBack={() => setStep(1)} />}
      {step === 3 && <Step3Payment onNext={() => setStep(4)} onBack={() => setStep(2)} />}
      {step === 4 && <Step4PaymentProof onNext={() => setStep(5)} onBack={() => setStep(3)} />}
    </div>
  );
}

function AdminFlow() {
  const [adminLoggedIn, setAdminLoggedIn] = useState(() => {
    return localStorage.getItem("adminLoggedIn") === "true";
  });

  useEffect(() => {
    localStorage.setItem("adminLoggedIn", adminLoggedIn);
  }, [adminLoggedIn]);

  return (
    <Suspense fallback={<div style={{ padding: "40px", textAlign: "center", color: "#64748b" }}>Loading Admin Portal...</div>}>
      {adminLoggedIn ? (
        <AdminDashboard onLogout={() => setAdminLoggedIn(false)} />
      ) : (
        <AdminLogin onLogin={() => setAdminLoggedIn(true)} />
      )}
    </Suspense>
  );
}

function App() {
  const isPort3001 = window.location.port === "3001";
  const isAdminHost = window.location.hostname.startsWith("admin");
  const isAdminPortal = process.env.REACT_APP_PORTAL === "admin" || isPort3001 || isAdminHost;

  if (isAdminPortal) {
    return (
      <Router>
        <Routes>
          <Route path="/" element={<AdminFlow />} />
          <Route path="/admin" element={<AdminFlow />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Router>
    );
  }

  return (
    <Router>
      <Routes>
        <Route path="/" element={<OnboardingFlow />} />
        <Route path="/payment-status" element={<PaymentStatus />} />
        <Route path="/admin" element={<AdminFlow />} />
        <Route path="/buyer/login" element={<BuyerLogin />} />
        <Route path="/buyer/forgot-password" element={<BuyerForgotPassword />} />
        <Route path="/buyer/reset-password" element={<BuyerResetPassword />} />
        <Route path="/buyer/setup-password" element={<BuyerSetupPassword />} />
        <Route path="/buyer/dashboard" element={<BuyerDashboard />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Router>
  );
}

export default App;